/**
 * Connecting a phone line, at the moment one is needed (V1.2-UX-T06): the home checklist's step, a number to give an
 * agent, a campaign to start. In plain words: Swaralo sells no numbers, so calls come through a line the tenant
 * has, and testing in the browser needs none. Three ways, each to its page and guide, and "Do it later".
 */
import * as Dialog from "@radix-ui/react-dialog";
import { Link } from "@tanstack/react-router";
import { MessageCircle, Phone, Server } from "lucide-react";
import type { ReactNode } from "react";
import { useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { useApiQuery } from "./Page";
import { useBrand } from "./brand";

const GUIDES = "https://github.com/swaralo/swaralo/blob/main/docs/guides";
const WAYS: { id: string; icon: ReactNode; title: MessageId; body: MessageId; guide: string }[] = [
  { id: "provider", icon: <Phone size={18} aria-hidden="true" />, title: "line.provider", body: "line.providerBody", guide: "cpaas.md" },
  { id: "sip", icon: <Server size={18} aria-hidden="true" />, title: "line.sip", body: "line.sipBody", guide: "telephony.md" },
  { id: "whatsapp", icon: <MessageCircle size={18} aria-hidden="true" />, title: "line.whatsapp", body: "line.whatsappBody", guide: "whatsapp.md" },
];

/** Whether the tenant has connected a phone line yet; undefined while that is not known. */
export function useHasLine(): boolean | undefined {
  const onboarding = useApiQuery("onboarding", (api) => api.GET("/v1/tenant/onboarding"));
  return onboarding.data ? Boolean(onboarding.data.first_line_at) : undefined;
}

export function PhoneLineDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useT();
  const brand = useBrand(); // a brand's tenants get its own help, never Swaralo's guides
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-16 max-h-[85vh] w-full max-w-lg -translate-x-1/2 space-y-4 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold">{t("line.title")}</Dialog.Title>
          <Dialog.Description className="text-sm">{t("line.body")}</Dialog.Description>
          <ul className="space-y-3">
            {WAYS.map((way) => (
              <li key={way.id} className="flex gap-3 rounded-md border border-[var(--color-border)] p-3 text-sm">
                <span className="mt-0.5 text-[var(--color-primary)]">{way.icon}</span>
                <span className="flex-1 space-y-1">
                  <span className="block font-medium">{t(way.title)}</span>
                  <span className="block text-[var(--color-muted)]">{t(way.body)}</span>
                  <span className="flex flex-wrap gap-3">
                    <Link to="/trunks" onClick={() => onOpenChange(false)} className="text-[var(--color-primary)] underline">
                      {t("line.open")}
                    </Link>
                    {!brand && (
                      <a href={`${GUIDES}/${way.guide}`} target="_blank" rel="noreferrer" className="text-[var(--color-primary)] underline">
                        {t("line.guide", { title: t(way.title) })}
                      </a>
                    )}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>{t("line.later")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
